import type { VolatilityStrengthReport } from "epsm-api-types/models";
import type { PriceInfo } from "epsm-api-types/ws";
import type { UTCTimestamp } from "lightweight-charts";
import type {
	IContext,
	PineJS,
	StudyMetaInfo,
} from "@/charting_library/charting_library";
import { chartColors } from "@/components/widgets/Chart/const";
import { CHART_COLORS } from "@/shared/constants/common";
import { TIMEFRAME_MAP } from "../../../../services/PriceService/PriceService";
import { VolatilityStrengthService } from "../../../../services/VolatilityStrengthService/VolatilityStrengthService";
import { Subcharts, type Timeframes } from "../../../../types";
import {
	CustomIndicatorService,
	getDefaultParams,
} from "../CustomIndicatorService";

export class VolatilityStrengthIndicatorService extends CustomIndicatorService<VolatilityStrengthReport> {
	fieldMapper(item: VolatilityStrengthReport): PriceInfo<UTCTimestamp> {
		return {
			time: item.date.getTime(),
			close: item.volatility_strength,
		} as PriceInfo<UTCTimestamp>;
	}

	async getBarData(
		from: number,
		to: number,
		interval: Timeframes,
		ticker: string,
	): Promise<PriceInfo<UTCTimestamp>[]> {
		if (TIMEFRAME_MAP[interval].isIntraday) {
			return [];
		}
		const response = await VolatilityStrengthService.create().getData(
			{
				...getDefaultParams(from, to, ticker),
				fields: ["date", "volatility_strength"],
			},
			this.fieldMapper,
		);

		return response.data ?? [];
	}

	getPlotsData(PineJS: PineJS, context: IContext): number[] {
		const volatilityStrength = PineJS.Std.close(context);
		const colorId = volatilityStrength < 0 ? 0 : 1;

		return [volatilityStrength, 0.25, -0.25, colorId];
	}

	get styles() {
		return {
			plot_0: {
				title: Subcharts.VolStrength,
				histogramBase: 0,
			},
			plot_1: {
				title: "Positive line",
				histogramBase: 0,
			},
			plot_2: {
				title: "Negative line",
				histogramBase: 0,
			},
		};
	}

	get plots(): StudyMetaInfo["plots"] {
		return [
			{
				id: "plot_0",
				type: "line",
			},
			{
				id: "plot_1",
				type: "line",
			},
			{
				id: "plot_2",
				type: "line",
			},
			{
				id: "plot_3",
				type: "colorer",
				target: "plot_0",
				palette: "paletteId1",
			},
		] as StudyMetaInfo["plots"];
	}

	get defaults(): StudyMetaInfo["defaults"] {
		return {
			styles: {
				plot_0: {
					plottype: 5,
					linestyle: 0,
					visible: true,
					transparency: 0,
				},
				plot_1: {
					plottype: 0,
					linestyle: 1,
					linewidth: 1,
					visible: true,
					transparency: 0,
					color: CHART_COLORS.GOLD,
				},
				plot_2: {
					plottype: 0,
					linestyle: 1,
					linewidth: 1,
					visible: true,
					transparency: 0,
					color: CHART_COLORS.GOLD,
				},
			},
			palettes: {
				paletteId1: {
					colors: {
						0: {
							color: chartColors.orange,
							width: 5,
							style: 0,
						},
						1: {
							color: chartColors.consolidation,
							width: 5,
							style: 0,
						},
					},
				},
			},
		};
	}

	get palettes(): StudyMetaInfo["palettes"] {
		return {
			paletteId1: {
				colors: {
					0: {
						name: "Down color",
					},
					1: {
						name: "Up color",
					},
				},
			},
		};
	}
}
